import {
  Box,
  Flex,
  SimpleGrid,
  Text,
  Button,
  Icon,
  Menu,
  MenuItem,
  MenuButton,
  MenuList,
  useToast,
  useTheme,
  Spinner,
} from '@chakra-ui/react';
import { FiPlusCircle } from 'react-icons/fi';
import { useCallback, useContext, useEffect, useState, useRef } from 'react';
import { Pagination } from 'src/components/atoms';
import { ListingCard } from 'src/components/molecules';
import {
  Category,
  RootCategory,
  Listing,
  AttributeOptions,
  ListingSelected,
  PaginatedListingsResponse,
} from 'src/api/v1-api';
import { apiRequest } from 'src/utils/fetchUtils';
import { useAttributeSelector } from 'src/contexts/AttributeSelector';
import { useAuth } from 'src/utils/auth';
import { useRouter } from 'next/router';
import { MarketplaceContext } from 'src/contexts/marketplace';
import { FaChevronDown } from 'react-icons/fa';
import CategorySelectorModal from 'src/components/molecules/category-selector-modal';
import { LISTINGS_PER_PAGE } from 'src/constants/marketplace';
import { useGlobalSearch } from 'src/contexts/globalSearch';
import * as Sentry from '@sentry/react';
interface Props {
  propListings?: PaginatedListingsResponse;
  fetchUrl: string | null;
  requiresAuth?: boolean;
  inventoryDisplay?: string;
  paginationStyle?: string;
  resultsPerPage?: number;
  actionButtons?: string;
  category?: Category | RootCategory | null;
  propNumDisplayListings?: number;
  userChangedPriceRange?: boolean;
  deactivatedListings?: boolean;
  onMountComplete?: () => void;
  onResponseMetadata?: (metadata: any) => void;
}

export const LISTING_SELECTION_LIMIT = 4;

const ListingsGrid = ({
  propListings,
  fetchUrl,
  userChangedPriceRange,
  requiresAuth = false,
  inventoryDisplay = '4_2_grid',
  paginationStyle = 'prev_next',
  resultsPerPage = LISTINGS_PER_PAGE,
  actionButtons = '',
  category = null,
  propNumDisplayListings = LISTINGS_PER_PAGE,
  deactivatedListings,
  onMountComplete,
  onResponseMetadata,
}: Props): JSX.Element => {
  const router = useRouter();
  const {
    selectedAttributes,
    priceRange,
    selectedSort,
    page,
    setPage,
    maxPrice,
    isInitialized,
    featured,
  } = useAttributeSelector();
  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const { getToken } = useAuth();
  const theme = useTheme();
  const { searchQuery } = useGlobalSearch();

  const listingsGridRef = useRef<HTMLDivElement>(null);
  const prevFiltersInitialized = useRef(false);

  if (typeof resultsPerPage === 'string') {
    resultsPerPage = parseInt(resultsPerPage);
  }

  const toast = useToast();
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [haveFetched, setHaveFetched] = useState<boolean>(false);
  const [usedPropListings, setUsedPropListings] = useState<boolean>(false);
  const [categorySelectorSelectedId, setCategorySelectorSelectedId] = useState<
    string | undefined
  >();
  const [categorySelectorListingId, setCategorySelectorListingId] = useState<string | undefined>();
  const [rerouting, setRerouting] = useState<boolean>(false);
  const [nextLink, setNextLink] = useState<string | null>(null);
  const [prevLink, setPrevLink] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState<number | null>(null);
  const [selectedListings, setSelectedListings] = useState<ListingSelected[]>([]);
  const [displayedListings, setDisplayedListings] = useState<Listing[]>([]);
  const [numDisplayListings, setNumDisplayListings] = useState<number>(propNumDisplayListings);

  const { marketplace } = useContext(MarketplaceContext);

  let columns = 4;
  if (inventoryDisplay === '3_3_grid') {
    columns = 3;
  } else if (inventoryDisplay === '2_4_grid') {
    columns = 2;
  }

  const selectedListingsCount = selectedListings.filter((x) => x.isSelected).length;

  // Load ISR pre-fetched listings on initial render when no filters are applied
  // This effect includes ALL dependencies to avoid stale closures, but guards prevent re-execution
  useEffect(() => {
    // Guard: Only execute once when conditions are right
    if (haveFetched || usedPropListings) return;

    // Check if URL has filter params - if so, wait for isInitialized to parse them
    const url = new URL(window.location.href);
    const hasUrlFilters =
      url.searchParams.has('filters') ||
      url.searchParams.has('search') ||
      url.searchParams.has('featured') ||
      (url.searchParams.has('page') && url.searchParams.get('page') !== '1');

    // Fast path: no URL filters - use propListings immediately (don't wait for context init)
    if (!hasUrlFilters && propListings?.results) {
      setListings(propListings.results);
      setNextLink(propListings.next);
      setPrevLink(propListings.previous);
      setTotalPages(propListings.count / resultsPerPage);
      setPage(propListings.page_number);
      setHaveFetched(true);
      setUsedPropListings(true);
      return;
    }

    // URL has filters - wait for initialization to parse them before deciding
    if (!isInitialized) return;

    // After initialization: verify context state matches clean state before using propListings
    const shouldUse =
      !!propListings?.results &&
      !hasUrlFilters &&
      page === 1 &&
      !Object.keys(selectedAttributes).length &&
      priceRange[0] === 0 &&
      (priceRange[1] === 0 || priceRange[1] === maxPrice) &&
      !selectedSort &&
      !searchQuery &&
      !featured;

    if (shouldUse) {
      setListings(propListings.results);
      setNextLink(propListings.next);
      setPrevLink(propListings.previous);
      setTotalPages(propListings.count / resultsPerPage);
      setPage(propListings.page_number);
      setHaveFetched(true);
      setUsedPropListings(true);
    }
  }, [
    propListings,
    isInitialized,
    page,
    selectedAttributes,
    priceRange,
    maxPrice,
    selectedSort,
    searchQuery,
    featured,
    haveFetched,
    usedPropListings,
    resultsPerPage,
    setPage,
  ]);

  const handleChangeSelected = (id: number, checked: boolean) => {
    setSelectedListings((st) =>
      st.map((l) => {
        if (l.listingId === id) {
          return { ...l, isSelected: checked };
        }
        return l;
      }),
    );
  };

  const fetchListings = useCallback(
    async (
      url: string,
      filters: AttributeOptions = {},
      priceRange: [number, number],
      page: number,
    ) => {
      if (!loading && page > 0) {
        setLoading(true);
        const params = new URLSearchParams();

        // If there's a price range and the user has set a value, add it to the params
        if (priceRange[0] !== 0 || (priceRange[1] !== 0 && priceRange[1] != maxPrice)) {
          params.append('price', `${priceRange[0]}-${priceRange[1]}`);
        }

        // If featured listings are requested, add it to the params
        if (featured) {
          params.append('is_featured', 'true');
        }

        // Convert filters object to query parameters
        for (const [key, values] of Object.entries(filters)) {
          if (key === 'page' && url.includes('page')) {
            continue;
          }
          for (const value of values) {
            if (value.toString().length > 0) {
              params.append(key, value.toString());
            }
          }
        }
        const paramsString = params.toString();
        const fullUrl = paramsString ? `${url}&${paramsString}` : url;

        try {
          let token = null;
          if (requiresAuth) {
            token = await getToken();
          }

          const response = await apiRequest('GET', fullUrl, token);
          setListings(response.results);
          setSelectedListings((st) => {
            const ids = new Set(st.map((d) => d.listingId));
            if (!response?.results) {
              return st;
            }
            let responseFiltered = response.results.filter((x: Listing) => !ids.has(x.id));
            responseFiltered = responseFiltered.map((x: Listing) => ({
              listingId: x.id,
              isSelected: false,
            }));
            return [...st, ...responseFiltered];
          });
          setNextLink(response.next);
          setPrevLink(response.previous);
          setTotalPages(response.count / resultsPerPage);
          setPage(response.page_number);
          setHaveFetched(true);

          if (onResponseMetadata) {
            onResponseMetadata(response);
          }
        } catch (error) {
          Sentry.captureException(error, {
            tags: {
              component: 'ListingsGrid',
              marketplaceId: marketplace?.id,
              categoryId: category?.id,
              page: page,
              params: paramsString,
              url: fullUrl,
            },
          });
        } finally {
          setLoading(false);
        }
        if (userChangedPriceRange || searchQuery != '') {
          listingsGridRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
      }
    },
    [
      getToken,
      loading,
      requiresAuth,
      userChangedPriceRange,
      searchQuery,
      featured,
      onResponseMetadata,
    ],
  );

  useEffect(() => {
    if (listings) {
      setDisplayedListings(listings.slice(0, numDisplayListings));
    }

    // If the number of displayed listings is less than LISTINGS_PER_PAGE,
    // and the next link is not null, trigger a Sentry event
    if (numDisplayListings < LISTINGS_PER_PAGE && nextLink) {
      Sentry.captureEvent({
        message: 'ListingsGrid: Number of displayed listings is less than LISTINGS_PER_PAGE',
        extra: {
          nextLink,
          categoryId: category?.id,
          marketplaceId: marketplace?.id,
          page,
          numDisplayListings,
        },
      });
    }
  }, [listings, numDisplayListings]);

  useEffect(() => {
    // We need to avoid loading when page is 0 or invalid.
    if (!page || page < 1) {
      return;
    }
    setRerouting(false);

    const url = `${fetchUrl}/?page_size=${resultsPerPage}&page=${page}${
      selectedSort ? `&sort=${selectedSort}` : ''
    }${searchQuery ? `&search=${searchQuery}` : ''}`;

    // Check if URL has filters that need to be parsed first
    const windowUrl = new URL(window.location.href);
    const hasUrlFilters =
      windowUrl.searchParams.has('filters') ||
      windowUrl.searchParams.has('search') ||
      windowUrl.searchParams.has('featured');

    // If URL has filters, MUST wait for context to parse them before fetching
    // Otherwise we'll fetch with empty filters
    const shouldFetchListings =
      !url.includes('undefined') &&
      inventoryDisplay !== 'None' &&
      !haveFetched &&
      (isInitialized || (!hasUrlFilters && propListings && page > 1));

    if (shouldFetchListings) {
      fetchListings(url, selectedAttributes, priceRange, page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedAttributes,
    priceRange,
    fetchUrl,
    page,
    selectedSort,
    haveFetched,
    searchQuery,
    isInitialized,
    featured,
  ]);

  // Mark filters as initialized after first render with initialized context
  // Now that ISR loading waits for isInitialized, we don't need to clear listings here
  useEffect(() => {
    if (isInitialized && !filtersInitialized) {
      setFiltersInitialized(true);
      // Set the ref here to ensure it's true even if no filter changes occur initially
      prevFiltersInitialized.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, filtersInitialized]);

  useEffect(() => {
    // Only reset page to 1 when filters change AFTER initial URL parsing is complete
    // Skip on first initialization to preserve page number from URL
    if (haveFetched && filtersInitialized && prevFiltersInitialized.current) {
      setPage(1);
      setHaveFetched(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAttributes, priceRange, selectedSort, searchQuery, featured]);

  const updateQueryParamAndContextPage = (link?: string | null, calculatedPage?: number) => {
    let pageNumber = '';

    if (link) {
      try {
        pageNumber = new URL(link).searchParams.get('page') ?? '1';
      } catch (error) {
        pageNumber = link.split('page=')[1];
      }
    } else if (calculatedPage) {
      pageNumber = calculatedPage.toString();
    } else {
      return;
    }

    let pageQueryName = 'page';
    if (deactivatedListings) {
      pageQueryName = 'deactivated_listings_page';
    }
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set(pageQueryName, pageNumber);
    setPage(parseInt(pageNumber));
    setNumDisplayListings(propNumDisplayListings);
    const searchParams = new URLSearchParams();
    currentUrl.searchParams.forEach((value, key) => {
      searchParams.append(key, value);
    });
    const newUrl = `${currentUrl.origin}${currentUrl.pathname}?${searchParams.toString()}`;
    window.history.replaceState({ ...window.history.state, as: newUrl, url: newUrl }, '', newUrl);
    setHaveFetched(false);
  };

  const handleNextPage = () => {
    const totalPageCount = Math.ceil(totalPages ?? 0);
    const hasNext = nextLink !== null || (page < totalPageCount && totalPages !== null);

    if (hasNext) {
      setRerouting(true);
      if (nextLink) {
        updateQueryParamAndContextPage(nextLink);
      } else {
        // Calculate next page when link is not available
        updateQueryParamAndContextPage(null, page + 1);
      }
      listingsGridRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handlePrevPage = () => {
    const hasPrev = prevLink !== null || (page > 1 && totalPages !== null);

    if (hasPrev) {
      setRerouting(true);
      if (prevLink) {
        updateQueryParamAndContextPage(prevLink);
      } else {
        // Calculate previous page when link is not available
        updateQueryParamAndContextPage(null, page - 1);
      }
      listingsGridRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const removeListing = (id: number) => {
    setSelectedListings((prevSelected) =>
      prevSelected.filter((selListing) => selListing.listingId !== id),
    );
    setListings((prevListings) => prevListings.filter((listing) => listing.id !== id));
  };

  const removeMultipleListings = (idsToRemove: number[]) => {
    setSelectedListings((prevSelected) =>
      prevSelected.filter((selListing) => !idsToRemove.includes(selListing.listingId)),
    );
    setListings((prevListings) =>
      prevListings.filter((listing) => !idsToRemove.includes(listing.id)),
    );
  };

  const handleBulkAction = async (action: string) => {
    const selectedListingsIds = selectedListings
      .filter((x) => x.isSelected)
      .map((x) => x.listingId);

    if (!selectedListingsIds || selectedListingsIds.length === 0) {
      toast({
        title: 'No listings selected',
        description: 'Please select listings to perform bulk actions.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (selectedListingsIds.length > 0) {
      const token = await getToken();
      const url = `${process.env.NEXT_PUBLIC_API_HOST}/listings/${marketplace?.id}/bulk-${action}/`;
      try {
        setLoading(true);
        await apiRequest('POST', url, token, {
          listing_ids: selectedListingsIds,
          action,
        });
        setLoading(false);

        // Show success toast
        const actionText = action === 'approve' ? 'approved' : 'denied';
        const listingText = selectedListingsIds.length === 1 ? 'listing' : 'listings';
        toast({
          title: `Listings ${actionText}`,
          description: `${selectedListingsIds.length} ${listingText} successfully ${actionText}.`,
          status: 'success',
          duration: 4000,
          isClosable: true,
        });

        // Check if all listings on current page will be removed before updating state
        const remainingListings = listings?.filter(
          (listing) => !selectedListingsIds.includes(listing.id),
        );

        // Remove approved/denied listings from the grid using batched update
        removeMultipleListings(selectedListingsIds);

        // If no listings remain and we're not on page 1, navigate to previous page
        if (remainingListings?.length === 0 && page > 1) {
          handlePrevPage();
        }
      } catch (error) {
        const actionText = action === 'approve' ? 'approve' : 'deny';
        const listingText = selectedListingsIds.length === 1 ? 'listing' : 'listings';
        toast({
          title: `Failed to ${actionText} listings`,
          description: `Could not ${actionText} ${selectedListingsIds.length} ${listingText}. Please try again.`,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        Sentry.captureException(error, {
          tags: {
            component: 'ListingsGrid',
          },
          extra: {
            actionText: actionText,
            listingText: listingText,
            selectedListingsIds: selectedListingsIds,
            marketplaceId: marketplace?.id,
            selectedListingsLength: selectedListingsIds.length,
          },
        });
      }
      setLoading(false);
    }
  };

  const updateCategoryListingHandler = async (categoryId: string) => {
    if (!categoryId || !categorySelectorListingId) return;

    setLoading(true);
    const token = await getToken();

    try {
      const url = `${process.env.NEXT_PUBLIC_API_HOST}/listings/${categorySelectorListingId}/${marketplace?.id}/update_category/`;
      const payload = {
        category_id: categoryId,
      };
      const updatedListing: Listing = await apiRequest('PUT', url, token, payload);
      if (listings) {
        const updatedListings = listings.map((listing: Listing) =>
          listing.id === updatedListing.id
            ? {
                ...listing,
                category_name: updatedListing?.category?.name,
                category_id: updatedListing?.category?.id,
                category_type: updatedListing?.category?.type,
              }
            : listing,
        );
        setListings(updatedListings);
      }

      toast({
        title: 'Success',
        description: 'Re-populating listing attributes may take a minute',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Error updating listing category';
      toast({
        title: 'Something went wrong, please try again',
        status: errorMessage,
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const anySelected = selectedListings.some((x: ListingSelected) => x.isSelected);

  // Determine sticky top position based on layout
  // AdminRouteLayout (slug_admin pages) has a sticky top bar, so we need to position below it
  // MarketplaceAdminLayout (regular admin pages) doesn't, so we can use top={0}
  const isAdminRoute = router.pathname.startsWith('/slug_admin');
  const stickyTopPosition = isAdminRoute ? { base: '57px', md: '65px' } : 0;

  const handlePageEmpty = useCallback(() => {
    // If no listings remain and we're not on page 1, go to previous page
    if (listings && listings.length === 0 && page > 1) {
      handlePrevPage();
    }
  }, [listings, page, handlePrevPage]);

  useEffect(() => {
    if (onMountComplete && haveFetched) {
      onMountComplete();
    }
  }, [onMountComplete, haveFetched]);

  // Handle empty page after listings are removed
  useEffect(() => {
    if (haveFetched && listings && listings.length === 0 && page > 1) {
      handlePageEmpty();
    }
  }, [listings, page, haveFetched, handlePageEmpty]);

  return (
    <Flex direction="column" width="100%" gap={{ base: 6, md: 10 }} pb={8} data-test="listing-view">
      <Box ref={listingsGridRef} position="relative">
        {actionButtons && actionButtons === 'pendingListings' && (
          <Flex
            id="bulk-actions-bar"
            marginTop={4}
            marginBottom={6}
            position={'sticky'}
            paddingY={'8px'}
            paddingLeft={'4px'}
            top={stickyTopPosition}
            zIndex={4}
            w="100%"
            alignItems="center"
            bgColor={'blackAlpha.300'}
            borderRadius={4}
          >
            <Menu>
              <MenuButton as={Button} rightIcon={<FaChevronDown />}>
                Bulk Actions
              </MenuButton>
              <MenuList zIndex={3}>
                <MenuItem
                  isDisabled={!anySelected}
                  onClick={() => {
                    handleBulkAction('approve');
                  }}
                >
                  Approve Selected
                </MenuItem>
                <MenuItem
                  isDisabled={!anySelected}
                  onClick={() => {
                    handleBulkAction('deny');
                  }}
                >
                  Deny Selected
                </MenuItem>
              </MenuList>
            </Menu>
          </Flex>
        )}
        <SimpleGrid
          position="relative"
          columns={{
            base: 2,
            md: 2,
            lg: 4,
          }}
          columnGap={{ base: 2, md: 4 }}
          rowGap={{ base: 2, md: 4 }}
          minHeight={14}
        >
          {!loading && haveFetched && listings && listings.length === 0 && (
            <Box
              gridColumn="span 4/span 4"
              textAlign="center"
              p={8}
              mt={4}
              border="1px solid"
              borderRadius={theme.border_radius.border_radius_2}
              borderColor="gray.300"
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
            >
              <Text fontSize="lg" color="gray">
                No listings yet
              </Text>
              {category?.functional?.show_create_listing_button && (
                <Button
                  mt={8}
                  colorScheme="primary"
                  onClick={() => router.push(`/listing/create?category=${category?.id}`)}
                >
                  <Icon as={FiPlusCircle} mr="2" />
                  {category?.functional?.text_overrides?.add_listing_btn || 'Add Listing'}
                </Button>
              )}
            </Box>
          )}
          {displayedListings &&
            displayedListings.length > 0 &&
            displayedListings.map((listing) => (
              <ListingCard
                key={listing.id}
                listing={listing}
                actionButtons={actionButtons}
                removeListing={removeListing}
                category={category}
                loading={loading}
                handleChangeSelected={handleChangeSelected}
                listingSelected={selectedListings.find((x) => x.listingId === listing.id)}
                onPageEmpty={handlePageEmpty}
                onCategoryEditClick={(
                  listingId: number | undefined,
                  categoryId: number | undefined,
                ) => {
                  setCategorySelectorListingId(listingId?.toString());
                  setCategorySelectorSelectedId(categoryId?.toString());
                }}
                columns={columns}
              />
            ))}
        </SimpleGrid>
        {(loading || rerouting) && (
          <Box
            pt={8}
            pb={8}
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            display="flex"
            justifyContent="center"
            alignItems="center"
            backgroundColor={
              displayedListings && displayedListings.length > 0
                ? 'rgba(255,255,255,0.7)'
                : 'transparent'
            }
          >
            <Spinner />
          </Box>
        )}
      </Box>
      <Pagination
        nextLink={nextLink}
        prevLink={prevLink}
        onNext={handleNextPage}
        onPrev={handlePrevPage}
        pageNumber={(page ?? 1).toString()} // Use nullish coalescing to provide a default of 1
        totalPages={totalPages}
      />
      <CategorySelectorModal
        isOpen={!!categorySelectorSelectedId}
        onClose={() => setCategorySelectorSelectedId(undefined)}
        onUpdateCategory={updateCategoryListingHandler}
        selectedCategoryId={categorySelectorSelectedId}
      />
    </Flex>
  );
};

export default ListingsGrid;
